import React, {useEffect, useState} from "react";
import {Skeleton, Box, Paper, Stack, useTheme, alpha} from "@mui/material";
import {useNavigate, useLocation} from "react-router-dom";
import CustomButton from "../../../core/components/ui/CustomButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useBooking from "../hooks/useBooking";
import CustomTypography from "../../../core/components/ui/CustomTypography";
import CustomChipComponent from "../../../core/components/common/custom-chip.component";
import moment from "moment";
import AddIcon from "@mui/icons-material/Add";
import AddVehicleDialog from "./add-vehicle.dialog";
import useGatePass from "../../GatePassPlugin/hooks/useGatePass";
import VehicleItemComponent from "./vehicle-item.component";
import useGatePassStore from "../../GatePassPlugin/zustand/useGatePassStore";
import {RBACUIGuard} from "../../RBACPlugin";
import {permissions} from "../../../core/components/common/variables/static_variables";


const ViewBookingComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const {isFetchingBookingDetails, bookingDetailsData, viewBooking} = useBooking();
    const {isListingVehicleBooking, vehicleListBooking, getListVehicleBooking} = useGatePass();
    const {isVehicleAddedToBooking, isOrgVehicleAddedToBooking} = useGatePassStore();

    const [open, setOpen] = useState(false);
    const [bookingDetails, setBookingDetails] = useState(null);
    const [vehicleListBookingData, setVehicleListBookingData] = useState([]);

    const bookingId = new URLSearchParams(location.search).get("id");
    const rbacStorage = JSON.parse(localStorage.getItem("rbac-storage"));
    const storedOrgIdFromRBACForBusiness = rbacStorage?.state?.currentProfile?.orgId || null;
    const orgIdFromUrlForPortEmployee = new URLSearchParams(location.search).get("org_uid");


    useEffect(() => {
        const organizationId = storedOrgIdFromRBACForBusiness || orgIdFromUrlForPortEmployee; 

        if (organizationId) {
            const parameters = {
                bookingUID: bookingId,
                orgUID: organizationId
            };

            viewBooking(parameters);
            getListVehicleBooking(parameters);

        } else {
            const parameters = {bookingUID: bookingId};
        
            viewBooking(parameters);
            getListVehicleBooking(parameters);
        }
    }, [bookingId, storedOrgIdFromRBACForBusiness, orgIdFromUrlForPortEmployee]);

    useEffect(() => {
        if (vehicleListBooking) {
            setVehicleListBookingData(vehicleListBooking);
        }
    }, [vehicleListBooking])

    useEffect(() => {
        if (bookingDetailsData) {
            setBookingDetails(bookingDetailsData);
        }
    }, [bookingDetailsData])

    useEffect(() => {
        const organizationId = storedOrgIdFromRBACForBusiness || orgIdFromUrlForPortEmployee;

        if (organizationId && isOrgVehicleAddedToBooking) {
            const parameters = {
                bookingUID: bookingId,
                orgUID: organizationId
            };
            
            getListVehicleBooking(parameters);

        } else if (!organizationId && isVehicleAddedToBooking) {
            const parameters = {bookingUID: bookingId};
            getListVehicleBooking(parameters);
        }
    }, [isVehicleAddedToBooking, isOrgVehicleAddedToBooking, storedOrgIdFromRBACForBusiness, orgIdFromUrlForPortEmployee])


    const handleOpenAddVehicle = () => {
        setOpen(true);
    }

    const handleBack = () => {
        if (orgIdFromUrlForPortEmployee) {
            navigate(`/organization/view?org_uid=${orgIdFromUrlForPortEmployee}`);

        } else {
            navigate("/bookings");
        }
    }


    return (
        <>  
            <CustomButton 
                variant="text" 
                startIcon={<ArrowBackIcon fontSize="small" />}
                label="Back to Booking List"
                customStyles={{fontSize: 14, ml: -2}}
                onClick={handleBack}
            />

            <Box sx={{textAlign: "start", mt: 2}}>
                <CustomTypography 
                    variant="h3"
                    text={"Booking Details"}
                    customStyles={{
                        marginBottom: 0, 
                    }}
                />

                <CustomTypography 
                    variant="subtitle1"
                    text={"View and manage booking information"}
                    customStyles={{
                        marginBottom: 0, 
                    }}
                />
            </Box>

            <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px", mt: 3}}>
                <Stack direction={{xs: "column", md: "column"}} spacing={2} sx={{mb: 3}}>
                    <Stack spacing={0} flex={1}>
                        <CustomTypography 
                            variant="caption" 
                            text={"Booking Reference"} 
                            customStyles={{
                                marginBottom: 0, 
                            }}
                        />

                        {(isFetchingBookingDetails) ?
                            <Skeleton variant="text" height="35px" width="34%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                        :
                            <CustomTypography
                                variant="body1"
                                text={(bookingDetails?.booking_uid)}
                                customStyles={{
                                    marginBottom: 0, 
                                    fontWeight: 600,
                                }}
                            />
                        }
                    </Stack>
                </Stack>

                <Stack direction={{xs: "column", md: "row"}} spacing={4}>
                        <Stack spacing={2} flex={1} direction={"column"}>
                            <Box>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Type"} 
                                    customStyles={{
                                        marginBottom: 0, 
                                    }}
                                />

                                {(isFetchingBookingDetails) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomTypography
                                        variant="body1"
                                        text={(bookingDetails?.booking_type)}
                                        customStyles={{
                                            marginBottom: 0, 
                                            fontWeight: 500,
                                        }}
                                    />
                                }
                            </Box>

                            <Box>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Purpose"} 
                                    customStyles={{
                                        marginBottom: 0, 
                                    }}
                                />

                                {(isFetchingBookingDetails) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomTypography
                                        variant="body1"
                                        text={(bookingDetails?.purpose)}
                                        customStyles={{
                                            marginBottom: 0, 
                                            fontWeight: 500,
                                        }}
                                    />
                                }
                            </Box>
                        </Stack>

                        <Stack spacing={2} flex={1}>
                            <Box sx={{display: "inline-grid"}}>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Entry"}
                                    customStyles={{
                                        marginBottom: 0, 
                                    }} 
                                />

                                {(isFetchingBookingDetails) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomTypography
                                        variant="body1"
                                        text={moment(bookingDetails?.valid_from).format("MM/DD/YYYY")}
                                        customStyles={{
                                            marginBottom: 0, 
                                            fontWeight: 500,
                                        }}
                                    />
                                }
  
                            </Box>

                            <Box>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Exit"} 
                                    customStyles={{
                                        marginBottom: 0, 
                                    }}
                                />

                                {(isFetchingBookingDetails) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomTypography
                                        variant="body1"
                                        text={moment(bookingDetails?.valid_to).format("MM/DD/YYYY")}
                                        customStyles={{
                                            marginBottom: 0, 
                                            fontWeight: 500,
                                        }}
                                    />    
                                }  
                            </Box>
                        </Stack>

                        <Stack spacing={2} flex={1}>
                            <Box sx={{display: "inline-grid"}}>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Status"} 
                                    customStyles={{
                                        marginBottom: 0, 
                                    }}
                                />

                                {(isFetchingBookingDetails) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomChipComponent category="bookingStatus"
                                        label={(bookingDetails?.status === "Draft") ? "Draft" 
                                            : 
                                                (bookingDetails?.status === "Pending for Approval") ? "Pending for Approval"
                                            :
                                                (bookingDetails?.status === "Scheduled") ? "Scheduled"
                                            :
                                                (bookingDetails?.status === "Cancelled") ? "Cancelled"
                                            :
                                                (bookingDetails?.status === "Overdue") ? "Overdue"
                                            :
                                                (bookingDetails?.status === "Completed") ? "Completed"
                                            :
                                                (bookingDetails?.status === "Rejected") ? "Rejected"
                                            :
                                                bookingDetails?.status
                                        }
                                    />
                                }
                            </Box>
                        </Stack>
                </Stack>           
            </Paper>

            <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px", mt: 3}}>
                <Stack direction={{xs: "column", md: "column"}} spacing={2} sx={{mb: 3}}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flex={1}>
                        <Box sx={{ textAlign: "start" }}>
                            <CustomTypography 
                                variant="h3"
                                text={"Vehicles"}
                                customStyles={{
                                    marginBottom: 0, 
                                    fontSize: 20,
                                    fontWeight: 700
                                }}
                            />

                            <CustomTypography 
                                variant="caption"
                                text={"Vehicles associated with this booking"}
                                customStyles={{
                                    marginBottom: 0,
                                    fontSize: 14 
                                }}
                            />
                        </Box>

                        <RBACUIGuard requiredPermission={permissions.gatepass.GENERATE_VEHICLE_GATEPASS}>
                            <CustomButton 
                                variant={"contained"}
                                label={"Add Vehicle"}
                                startIcon={<AddIcon fontSize="small" />}
                                onClick={handleOpenAddVehicle}                            
                            /> 
                        </RBACUIGuard>
                    </Stack>
            
                    <Box sx={{mt: "24px !important"}}>
                        {(vehicleListBookingData?.length > 0) ?
                            vehicleListBookingData?.map((bookingVehicle, index) => (
                                <VehicleItemComponent 
                                    index={index}
                                    bookingVehicle={bookingVehicle}  
                                    vehicleListBookingData={vehicleListBookingData} 
                                />
                            ))
                        :
                            ((!isListingVehicleBooking && vehicleListBookingData?.length === 0) && 
                                <Stack sx={{margin: "auto !important"}}>
                                    <Box sx={{mt: 3, mb: 4}}>
                                        <CustomTypography
                                            variant="body1"
                                            text={`No vehicles added to this booking. Click "Add Vehicle" to add one.`}
                                            customStyles={{
                                                marginBottom: 0, 
                                                fontWeight: 400,
                                                textAlign: "center"
                                            }}
                                        />
                                    </Box>
                                </Stack>
                            )
                        }
                    </Box>
                </Stack>
            </Paper>

            {open &&
                <AddVehicleDialog 
                    open={open}
                    setOpen={setOpen}
                    bookingDetails={bookingDetails}
                />
            }
        </>
    )
}

export default ViewBookingComponent